import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError } from "../../lib/api.js";

type RoleDef = Awaited<ReturnType<typeof api.rbacRoles>>["roles"][number];
type PermDef = Awaited<ReturnType<typeof api.rbacPermissions>>["permissions"][number];

export default function AdminRoles() {
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [mapping, setMapping] = useState<Record<string, string[]>>({});
  const [permissions, setPermissions] = useState<PermDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = useState<"all" | "platform" | "workspace">("all");
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.rbacRoles(), api.rbacPermissions()])
      .then(([r, p]) => {
        setRoles(r.roles);
        setMapping(r.mapping);
        setPermissions(p.permissions);
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  const visibleRoles = useMemo(
    () => roles.filter((r) => scopeFilter === "all" || r.scope === scopeFilter),
    [roles, scopeFilter]
  );

  const permByKey = useMemo(() => {
    const m = new Map<string, PermDef>();
    for (const p of permissions) m.set(p.key, p);
    return m;
  }, [permissions]);

  const categoriesForRole = (roleKey: string) => {
    const keys = mapping[roleKey] ?? [];
    const byCat = new Map<string, PermDef[]>();
    for (const k of keys) {
      const p = permByKey.get(k);
      if (!p) continue;
      const list = byCat.get(p.category) ?? [];
      list.push(p);
      byCat.set(p.category, list);
    }
    return [...byCat.entries()].sort(([a], [b]) => a.localeCompare(b));
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Roles & permissions</h1>
          <p>System-defined platform and workspace roles, with their permission mappings.</p>
        </div>
        <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value as typeof scopeFilter)}>
          <option value="all">All scopes</option>
          <option value="platform">Platform</option>
          <option value="workspace">Workspace</option>
        </select>
      </header>

      {err && <div className="alert alert-error">{err}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 320px) 1fr", gap: 16 }}>
          <aside className="card" style={{ padding: 0 }}>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {visibleRoles.map((r) => {
                const active = selectedRole === r.key;
                return (
                  <li key={r.key}>
                    <button
                      type="button"
                      onClick={() => setSelectedRole(r.key)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 12px",
                        border: 0,
                        borderBottom: "1px solid #e5e7eb",
                        background: active ? "#eef2ff" : "transparent",
                        cursor: "pointer"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <strong>{r.name}</strong>
                        <span className={`pill ${r.scope === "platform" ? "pill-info" : "pill-muted"}`}>
                          {r.scope}
                        </span>
                      </div>
                      <div className="soft" style={{ fontSize: 12 }}><code>{r.key}</code></div>
                    </button>
                  </li>
                );
              })}
              {visibleRoles.length === 0 && <li className="empty" style={{ padding: 16 }}>No roles match.</li>}
            </ul>
          </aside>

          <section className="card">
            {selectedRole ? (
              (() => {
                const role = roles.find((r) => r.key === selectedRole);
                if (!role) return <div className="empty">Role not found.</div>;
                const cats = categoriesForRole(role.key);
                return (
                  <div className="card-body">
                    <h2 style={{ marginTop: 0 }}>{role.name}</h2>
                    <div className="soft" style={{ marginBottom: 8 }}>
                      <code>{role.key}</code> · <span className="pill pill-muted">{role.scope}</span>
                      {role.legacyAliases?.length ? (
                        <> · legacy: {role.legacyAliases.map((a) => <code key={a} style={{ marginLeft: 4 }}>{a}</code>)}</>
                      ) : null}
                    </div>
                    <p>{role.description}</p>
                    <h3>Permissions ({(mapping[role.key] ?? []).length})</h3>
                    {cats.length === 0 ? <div className="empty">No permissions mapped.</div> : (
                      <div style={{ display: "grid", gap: 12 }}>
                        {cats.map(([cat, perms]) => (
                          <div key={cat}>
                            <div className="sidebar-section-title" style={{ marginBottom: 4 }}>{cat}</div>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {perms.map((p) => (
                                <li key={p.key} style={{ marginBottom: 4 }}>
                                  <code>{p.key}</code>{" "}
                                  <span className="soft" style={{ fontSize: 12 }}>— {p.description}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="card-body empty">Select a role to see its description and permission grants.</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
